/**
 * cosmic-collection.js
 * Custom AJAX filtering and sorting for the Cosmic Collection section.
 * Does NOT depend on Dawn's facets.js or FacetFiltersForm web component.
 */

(function () {
  'use strict';

  var CosmicCollection = {
    sectionId: null,
    container: null,
    grid: null,
    isLoading: false,
    infiniteScrollObserver: null,

    init: function () {
      var wrapper = document.querySelector('[data-cosmic-collection]');
      if (!wrapper) return;

      this.sectionId = wrapper.dataset.sectionId;
      this.container = wrapper;
      this.grid = document.getElementById('CosmicProductGrid');

      this.bindEvents();
      this.bindFilterToggle();
      this.initViewSwitcher();
      this.initWishlist();
      this.initQuickBag();
      this.shuffleIfNeeded();
      this.initAnimations();
      this.initInfiniteScroll();
    },

    initAnimations: function () {
      var reveals = document.querySelectorAll('.cosmic-animate-init');
      if (!reveals.length) return;

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });

      reveals.forEach(function (el) {
        observer.observe(el);
      });
    },

    bindEvents: function () {
      var self = this;

      // Sort dropdown in bar
      var sortBar = document.getElementById('CosmicSortBar');
      if (sortBar) {
        sortBar.addEventListener('change', function () {
          self.onFormChange();
        });
      }

      // Sort radios inside drawer
      document.addEventListener('change', function (e) {
        if (e.target && e.target.classList.contains('cosmic-sort-radio')) {
          var sortVal = e.target.getAttribute('data-sort-value');
          // Sync bar select
          var sortBar2 = document.getElementById('CosmicSortBar');
          if (sortBar2) sortBar2.value = sortVal;
          self.onFormChange();
        }
      });

      // Filter checkboxes / range inputs inside drawer
      var filterForm = document.getElementById('CosmicFilterForm');
      if (filterForm) {
        filterForm.addEventListener('change', function () {
          self.onFormChange();
        });
      }

      // Price range inputs (debounce these)
      var priceInputs = document.querySelectorAll('.cosmic-price-input');
      priceInputs.forEach(function (input) {
        input.addEventListener('change', debounce(function () {
          self.onFormChange();
        }, 500));
      });

      // Active filter pill remove links
      document.addEventListener('click', function (e) {
        var removeLink = e.target.closest('.cosmic-remove-filter');
        if (removeLink) {
          e.preventDefault();
          var url = removeLink.getAttribute('href');
          var searchParams = url.indexOf('?') > -1 ? url.split('?')[1] : '';
          self.renderPage(searchParams);
        }
      });

      // Clear all button
      document.addEventListener('click', function (e) {
        var clearAll = e.target.closest('.cosmic-clear-all');
        if (clearAll) {
          e.preventDefault();
          self.renderPage('');
        }
      });

      // Browser back/forward
      window.addEventListener('popstate', function (e) {
        var searchParams = e.state ? e.state.searchParams : window.location.search.slice(1);
        self.renderPage(searchParams, false);
      });
    },

    bindFilterToggle: function () {
      var toggleBtn = document.getElementById('CosmicFilterToggle');
      var drawer = document.getElementById('CosmicFilterDrawer');
      var overlay = document.getElementById('CosmicDrawerOverlay');
      var closeBtn = document.getElementById('CosmicFilterClose');

      if (!toggleBtn || !drawer) return;

      function openDrawer() {
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-open');
        if (overlay) overlay.classList.add('is-visible');
        document.body.style.overflow = 'hidden';
        drawer.focus();
      }

      function closeDrawer() {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-visible');
        document.body.style.overflow = '';
        if (toggleBtn) toggleBtn.focus();
      }

      // Close on Escape key
      document.addEventListener('keyup', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
          closeDrawer();
        }
      });

      toggleBtn.addEventListener('click', function () {
        if (drawer.classList.contains('is-open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', closeDrawer);
      }

      if (overlay) {
        overlay.addEventListener('click', closeDrawer);
      }

      // Apply button closes drawer and submits
      var applyBtn = document.getElementById('CosmicFilterApply');
      if (applyBtn) {
        applyBtn.addEventListener('click', function () {
          closeDrawer();
        });
      }
    },

    onFormChange: function () {
      var searchParams = this.buildSearchParams();
      this.renderPage(searchParams);
    },

    buildSearchParams: function () {
      var params = [];

      // Sort from bar select first, then drawer if bar not present
      var sortBar = document.getElementById('CosmicSortBar');
      var sortDrawer = document.getElementById('CosmicSortDrawer');
      var sortValue = '';
      if (sortBar) sortValue = sortBar.value;
      if (sortDrawer && sortDrawer.value) sortValue = sortDrawer.value;
      if (sortValue) params.push('sort_by=' + encodeURIComponent(sortValue));

      // Filter form (excludes sort radios which are handled by the bar select)
      var filterForm = document.getElementById('CosmicFilterForm');
      if (filterForm) {
        var formData = new FormData(filterForm);
        formData.forEach(function (value, key) {
          if (value !== '' && key !== 'sort_by_drawer') {
            params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
          }
        });
      }

      return params.join('&');
    },

    renderPage: function (searchParams, updateHistory) {
      if (this.isLoading) return;
      if (typeof updateHistory === 'undefined') updateHistory = true;

      var self = this;
      var url = window.location.pathname + '?section_id=' + this.sectionId + (searchParams ? '&' + searchParams : '');

      this.isLoading = true;
      this.setLoading(true);

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          // Swap product grid
          var newGrid = doc.getElementById('CosmicProductGrid');
          var currentGrid = document.getElementById('CosmicProductGrid');
          if (newGrid && currentGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;
          }

          // Swap active filters bar
          var newActivePills = doc.getElementById('CosmicActivePills');
          var currentActivePills = document.getElementById('CosmicActivePills');
          if (newActivePills && currentActivePills) {
            currentActivePills.innerHTML = newActivePills.innerHTML;
          }

          // Swap filter form content (to update counts)
          var newFilterBody = doc.getElementById('CosmicFilterBody');
          var currentFilterBody = document.getElementById('CosmicFilterBody');
          if (newFilterBody && currentFilterBody) {
            currentFilterBody.innerHTML = newFilterBody.innerHTML;
          }

          // Update product count
          var newCount = doc.getElementById('CosmicProductCount');
          var currentCount = document.getElementById('CosmicProductCount');
          if (newCount && currentCount) {
            currentCount.innerHTML = newCount.innerHTML;
          }

          // Update URL
          if (updateHistory) {
            history.pushState({ searchParams: searchParams }, '', window.location.pathname + (searchParams ? '?' + searchParams : ''));
          }

          self.isLoading = false;
          self.setLoading(false);
          self.initViewSwitcher(true);
          self.initWishlist();
          self.initQuickBag();
          self.shuffleIfNeeded();
          self.initAnimations();
          self.initInfiniteScroll();
        })
        .catch(function (err) {
          console.error('CosmicCollection: filter fetch failed', err);
          self.isLoading = false;
          self.setLoading(false);
        });
    },

    setLoading: function (loading) {
      var grid = document.getElementById('CosmicProductGrid');
      if (!grid) return;
      if (loading) {
        grid.style.opacity = '0.4';
        grid.style.pointerEvents = 'none';
        grid.style.transition = 'opacity 0.25s ease';
      } else {
        grid.style.opacity = '1';
        grid.style.pointerEvents = '';
        grid.style.transition = 'opacity 0.3s ease';
      }
    },

    shuffleIfNeeded: function () {
      if (this.container.dataset.shuffle !== 'true') return;
      var gridContainer = document.querySelector('.cosmic-product-grid-container');
      if (!gridContainer) return;

      var items = Array.from(gridContainer.querySelectorAll('.cosmic-grid-item'));
      if (items.length === 0) return;

      for (var i = items.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var temp = items[i];
        items[i] = items[j];
        items[j] = temp;
      }

      var perPage = parseInt(this.container.dataset.perPage, 10) || 16;
      
      items.forEach(function (item, index) {
        if (index >= perPage) {
          item.style.display = 'none';
          item.classList.add('cosmic-shuffled-hidden');
        } else {
          item.style.display = '';
          item.classList.remove('cosmic-shuffled-hidden');
        }
        gridContainer.appendChild(item);
      });

      var sentinel = document.querySelector('.cosmic-infinite-sentinel');
      if (sentinel) {
        if (items.length > perPage) {
          sentinel.style.display = '';
          sentinel.removeAttribute('data-next-url');
        } else {
          sentinel.style.display = 'none';
        }
      }
    },

    initInfiniteScroll: function () {
      var self = this;
      var sentinel = document.querySelector('.cosmic-infinite-sentinel');
      
      if (this.infiniteScrollObserver) {
        this.infiniteScrollObserver.disconnect();
      }

      if (!sentinel) return;
      
      // Ensure it is visible if there is a next url or if shuffle is true and we have hidden items
      if (sentinel.getAttribute('data-next-url') || (self.container.dataset.shuffle === 'true' && document.querySelectorAll('.cosmic-shuffled-hidden').length > 0)) {
        sentinel.style.display = '';
      }

      this.infiniteScrollObserver = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting && !self.isLoading) {
          self.loadNextPage();
        }
      }, { rootMargin: '400px' });

      this.infiniteScrollObserver.observe(sentinel);
    },

    loadNextPage: function () {
      var self = this;
      var sentinel = document.querySelector('.cosmic-infinite-sentinel');
      if (!sentinel) return;

      if (this.container.dataset.shuffle === 'true') {
        var hiddenItems = document.querySelectorAll('.cosmic-shuffled-hidden');
        if (hiddenItems.length === 0) {
          sentinel.style.display = 'none';
          return;
        }

        self.isLoading = true;
        sentinel.classList.add('is-loading');
        
        var perPage = parseInt(this.container.dataset.perPage, 10) || 16;
        var itemsToReveal = Array.from(hiddenItems).slice(0, perPage);
        
        setTimeout(function() {
          itemsToReveal.forEach(function(item) {
            item.style.display = '';
            item.classList.remove('cosmic-shuffled-hidden');
          });

          if (hiddenItems.length <= perPage) {
            sentinel.style.display = 'none';
          }

          self.isLoading = false;
          sentinel.classList.remove('is-loading');
          self.initAnimations();
        }, 200);
        
        return;
      }

      var nextUrl = sentinel.getAttribute('data-next-url');
      if (!nextUrl) {
        sentinel.style.display = 'none';
        return;
      }

      var self = this;
      this.isLoading = true;
      sentinel.classList.add('is-loading');

      var fetchUrl = nextUrl + (nextUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + this.sectionId;

      fetch(fetchUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          var newGridContainer = doc.querySelector('.cosmic-product-grid-container');
          var currentGridContainer = document.querySelector('.cosmic-product-grid-container');

          if (newGridContainer && currentGridContainer) {
            var items = newGridContainer.querySelectorAll('.cosmic-grid-item');
            items.forEach(function (item) {
              currentGridContainer.appendChild(item);
            });
          }

          var newSentinel = doc.querySelector('.cosmic-infinite-sentinel');
          if (newSentinel) {
            var newNextUrl = newSentinel.getAttribute('data-next-url');
            if (newNextUrl) {
              sentinel.setAttribute('data-next-url', newNextUrl);
            } else {
              sentinel.removeAttribute('data-next-url');
              sentinel.style.display = 'none';
            }
          } else {
            sentinel.style.display = 'none';
          }

          self.isLoading = false;
          sentinel.classList.remove('is-loading');
          self.initWishlist();
          self.initQuickBag();
          self.initAnimations();
        })
        .catch(function (err) {
          console.error('CosmicCollection: infinite scroll failed', err);
          self.isLoading = false;
          sentinel.classList.remove('is-loading');
        });
    },

    initViewSwitcher: function (skipStorageRead) {
      var switcher = document.querySelector('.cosmic-view-switcher');
      var gridContainer = document.getElementById('CosmicGridContainer') || document.querySelector('.cosmic-product-grid-container');
      if (!switcher || !gridContainer) return;

      var buttons = switcher.querySelectorAll('.cosmic-view-btn');
      var currentCols = localStorage.getItem('psygalaxy_grid_view') || '4';

      function applyView(cols) {
        gridContainer.classList.remove('cols--2', 'cols--4', 'cols--1');
        gridContainer.classList.add('cols--' + cols);
        buttons.forEach(function (btn) {
          if (btn.getAttribute('data-view-cols') === cols) {
            btn.classList.add('is-active');
          } else {
            btn.classList.remove('is-active');
          }
        });
      }

      applyView(currentCols);

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var cols = btn.getAttribute('data-view-cols');
          applyView(cols);
          try {
            localStorage.setItem('psygalaxy_grid_view', cols);
          } catch (err) {}
        });
      });
    },

    initWishlist: function () {
      var stored = [];
      try {
        stored = JSON.parse(localStorage.getItem('psygalaxy_wishlist') || '[]');
      } catch (e) {
        stored = [];
      }

      var wishlistBtns = document.querySelectorAll('.cosmic-card-wishlist');
      wishlistBtns.forEach(function (btn) {
        var id = btn.getAttribute('data-wishlist-id');
        if (stored.indexOf(id) > -1) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }

        btn.onclick = function (e) {
          e.preventDefault();
          e.stopPropagation();
          var pid = btn.getAttribute('data-wishlist-id');
          var idx = stored.indexOf(pid);
          if (idx > -1) {
            stored.splice(idx, 1);
            btn.classList.remove('is-active');
          } else {
            stored.push(pid);
            btn.classList.add('is-active');
          }
          try {
            localStorage.setItem('psygalaxy_wishlist', JSON.stringify(stored));
          } catch (err) {}
        };
      });
    },

    initQuickBag: function () {
      var self = this;
      var bagBtns = document.querySelectorAll('.cosmic-bag-btn');
      var closeBtns = document.querySelectorAll('.cosmic-quick-sizes-close');
      var sizeBtns = document.querySelectorAll('.cosmic-quick-size-btn');

      bagBtns.forEach(function (btn) {
        btn.onclick = function (e) {
          e.preventDefault();
          e.stopPropagation();

          var count = parseInt(btn.getAttribute('data-variants-count') || '1', 10);
          var productId = btn.getAttribute('data-product-id');
          var firstVariantId = btn.getAttribute('data-first-variant-id');

          if (count <= 1 && firstVariantId) {
            self.addToCart(firstVariantId, btn);
          } else {
            var sizeDrawer = document.getElementById('quick-sizes-' + productId);
            if (sizeDrawer) {
              document.querySelectorAll('.cosmic-quick-sizes.is-open').forEach(function (el) {
                if (el !== sizeDrawer) el.classList.remove('is-open');
              });
              sizeDrawer.classList.toggle('is-open');
            }
          }
        };
      });

      closeBtns.forEach(function (btn) {
        btn.onclick = function (e) {
          e.preventDefault();
          e.stopPropagation();
          var drawer = btn.closest('.cosmic-quick-sizes');
          if (drawer) drawer.classList.remove('is-open');
        };
      });

      sizeBtns.forEach(function (btn) {
        btn.onclick = function (e) {
          e.preventDefault();
          e.stopPropagation();
          var variantId = btn.getAttribute('data-variant-id');
          if (!variantId || btn.disabled) return;
          self.addToCart(variantId, btn);
        };
      });

      document.addEventListener('click', function (e) {
        if (!e.target.closest('.cosmic-quick-sizes') && !e.target.closest('.cosmic-bag-btn')) {
          document.querySelectorAll('.cosmic-quick-sizes.is-open').forEach(function (el) {
            el.classList.remove('is-open');
          });
        }
      });
    },

    addToCart: function (variantId, triggeringBtn) {
      var cartDrawer = document.querySelector('cart-drawer');
      if (triggeringBtn) {
        triggeringBtn.style.opacity = '0.4';
        triggeringBtn.style.pointerEvents = 'none';
      }

      var sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function' 
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; }).join(',') 
        : 'cart-drawer,cart-icon-bubble';

      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', sectionsToRender);
      formData.append('sections_url', window.location.pathname);

      var addUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (triggeringBtn) {
          triggeringBtn.style.opacity = '';
          triggeringBtn.style.pointerEvents = '';
        }
        document.querySelectorAll('.cosmic-quick-sizes.is-open').forEach(function (el) {
          el.classList.remove('is-open');
        });

        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          cartDrawer.renderContents(data);
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          var cartLink = document.querySelector('#cart-icon-bubble');
          if (cartLink) cartLink.click();
          else window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        console.error('CosmicCollection: add to cart failed', err);
        if (triggeringBtn) {
          triggeringBtn.style.opacity = '';
          triggeringBtn.style.pointerEvents = '';
        }
      });
    }
  };

  function debounce(fn, delay) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
    CosmicCollection.init();
  });
})();
